import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useRouteMatch } from 'react-router-dom';

import { getThemeVariant, THEME_VARIANTS } from 'core/components/theme/theme-variant';
import { getBackgroundPreference } from 'core/components/theme/background';
import { isCondensedLayout } from 'core/components/theme/condensed-layout';
import NeighborhoodArtwork from './assets/neighborhood.svg?react';
import GardenArtwork from './assets/garden.svg?react';

import { brandOpaqueBackgroundLogo, brandConstants, isBranchEnv } from 'brand-config';
import Header from '../header/header';
import { NavRail } from 'core/components/nav-rail';
import SystemStatus from 'core/components/system-status';
import ImpersonationBanner from 'core/components/impersonation-banner';
import AgencyClaimsBanner from 'core/components/agency-claims-banner';
import { ViewAsBanner } from 'core/components/view-as';
import useSession from 'core/hooks/use-session';

import { SiteHeaderContext } from './site-header-context';
import useStyles from './layout.styles';
import useGutterFloor from './use-gutter-floor';

const brand = brandConstants.SHORT_NAME?.toLowerCase();

const Layout = observer(({ children }) => {
  const session = useSession();
  const isCdComparison = !!useRouteMatch({
    path: [
      '/customer/:id/policy/:policyId/version-compare/:version',
      '/customer/:id/policy/:policyId/:policyPreviewId'
    ],
    exact: true
  });
  const [{ gutterArtwork }] = useState(getBackgroundPreference);
  const [navOpen, setNavOpen] = useState(false);
  const customized = session.isLoggedIn && getThemeVariant() === THEME_VARIANTS.RESKIN;
  const condensed = session.isLoggedIn && isCondensedLayout();
  const isArtworkPage = !!useRouteMatch({ path: ['/portal', '/quote', '/search'] });
  // The condensed layout has no side gutters; its nav carries the illustration instead.
  const showArtwork = customized && !condensed && gutterArtwork && isBranchEnv && isArtworkPage;
  const layoutRef = useRef(null);
  const footerHeight = useGutterFloor(showArtwork, layoutRef);
  const classes = useStyles({ showHeader: session.isLoggedIn, brand, isCdComparison, footerHeight, condensed }) || {};
  const [isSiteHeaderHidden, setSiteHeaderHidden] = useState(false);
  const siteHeaderContext = useMemo(() => ({ scrollContainerRef: layoutRef, setSiteHeaderHidden }), []);

  // Apply the impersonation grayscale to #root (rather than <body>) so MUI4
  // portal content — modals, tooltips, snackbars — which mounts directly on
  // <body> outside #root, isn't subject to `filter`-induced containing-block
  // changes for fixed positioning. Those portal surfaces will stay in color;
  // pair this with an explicit banner once that lands.
  useEffect(() => {
    document.getElementById('root')?.classList.toggle('is-impersonating', !!session.isImpersonating);
  }, [session.isImpersonating]);

  const page = (
    <div className={classes.layout} ref={layoutRef}>
      {showArtwork && (
        <div className={classes.gutterArtwork} aria-hidden="true">
          <NeighborhoodArtwork className={classes.leftArtwork} focusable="false" preserveAspectRatio="xMinYMax meet" />
          <GardenArtwork className={classes.rightArtwork} focusable="false" preserveAspectRatio="xMaxYMax meet" />
        </div>
      )}
      {session.isLoggedIn && <SystemStatus />}
      {session.isLoggedIn && <ImpersonationBanner />}
      {session.isLoggedIn && <AgencyClaimsBanner />}
      <div className={classNames(classes.container, { [classes.condensedContainer]: condensed })}>{children}</div>
    </div>
  );

  return (
    <SiteHeaderContext.Provider value={siteHeaderContext}>
      {session.isLoggedIn ? (
        // The condensed top bar holds the search and the nav toggle, so it stays when a page hides the site header.
        <Header onOpenNav={() => setNavOpen(true)} hidden={isSiteHeaderHidden && !condensed} />
      ) : (
        <img src={brandOpaqueBackgroundLogo} alt="gobranch" className={classes.logo} />
      )}
      {condensed && <ViewAsBanner />}
      {condensed ? (
        <div className={classes.condensedShell}>
          <NavRail mobileOpen={navOpen} onMobileClose={() => setNavOpen(false)} />
          {page}
        </div>
      ) : (
        page
      )}
    </SiteHeaderContext.Provider>
  );
});

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  classes: PropTypes.object
};

export default Layout;
